<template>
  <div
    v-if="cards.length"
    class="yzd-runtime"
    :style="gridStyle"
  >
    <Yurtzeiten_Template_Render
      v-for="(card, index) in cards"
      :key="`${card.key}-${renderSettings.cardPadding}-${renderSettings.spacing}`"
      class="yzd-runtime__card"
      :style="getCardStyle(index)"
      :template-id="templateId"
      :settings="renderSettings"
      :entries="card.entries"
      :max-scale="maxScale"
      :show-placeholders="false"
      :available-height="cardAvailableHeight"
      :scroll-speed="scrollSpeed"
      @natural-width="(width) => onCardNaturalWidth(card.key, width)"
    />
    <!-- Older saved designs do not carry the editor's authored content width.
         Measure their original {token} HTML once, outside layout, and share
         that one width with every card on the display. -->
    <Teleport to="body">
      <div
        v-if="!storedContentWidth"
        ref="widthProbeCanvas"
        class="yzd-width-probe yz-design-canvas"
        :style="widthProbeStyle"
        aria-hidden="true"
        @load.capture="scheduleWidthProbeMeasure"
      >
        <div class="yzd-width-probe__content">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="yzd-width-probe__document" v-html="activeSettings.html"></div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import Yurtzeiten_Template_Render from '@/components/yurtzeiten_N/Yurtzeiten_Template_Render.vue';
import filters from '@/filters';
import { getYurtzeitenCardCapacity_N } from '@/utils/yurtzeiten_design_render';
import { STORAGE_URL, YURTZEITEN_IMAGES_DIR } from '@/utils/const';
import { toWallClockDate } from '@/utils/wall_clock_date';
import { toYurtzeitenNameParts, yurtzeitenFullName } from '@/utils/yurtzeiten_name';
import {
  DEFAULT_YURTZEITEN_TEMPLATE_ID_N,
  YURTZEITEN_TEMPLATE_CONTENT_WIDTH,
  buildYurtzeitenDisplayCardSizes_N,
  defaultYurtzeitenTemplateSettings_N,
  findYurtzeitenTemplate_N,
  getYurtzeitenCardPaddingParts_N,
  normalizeYurtzeitenCardPadding_N,
  normalizeYurtzeitenCardSpacing_N,
  normalizeYurtzeitenContentWidth_N,
  normalizeYurtzeitenDesignSettings_N,
  resolveYurtzeitenDisplayColumns_N,
} from '@/utils/yurtzeiten_design_templates';
import { getCenteredCardPlacement, getCenteredCardTemplateColumns } from '@/utils/column_layout';

export default {
  name: 'Yurtzeiten_Display_Render',
  components: {
    Yurtzeiten_Template_Render,
  },
  props: {
    // Filtering and ordering belong to the display surface. Preserving that
    // order here makes the record-to-card mapping deterministic.
    records: {
      type: Array,
      default: () => [],
    },
    // One organization-wide row. Null is valid and selects built-in defaults.
    design: {
      type: Object,
      default: null,
    },
    maxScale: {
      type: Number,
      default: 1.35,
    },
    horizontal: {
      type: Boolean,
      default: false,
    },
    verticalScrolling: {
      type: Boolean,
      default: false,
    },
    // Pre-scale artboard width supplied by Display.vue. It lets horizontal
    // cards retain the authored column width inside a max-content strip.
    viewportWidth: {
      type: Number,
      default: 0,
    },
    // The artboard's height, in those same pre-scale units. A page is one row
    // of cards, so this is what each card has to fit into — and a card that
    // cannot gives its note a window and scrolls it instead of being shrunk or
    // cut off. Zero (the default) leaves every note at its full height.
    viewportHeight: {
      type: Number,
      default: 0,
    },
    // The pace the screen's other scrollers run at, in pre-scale pixels per
    // second, passed through so a scrolling note matches them.
    scrollSpeed: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      // What each rendered card actually measures, keyed by card. Read back only
      // for cards currently on screen, so a page that rotates away can't hold the
      // row open at its width.
      cardWidths: {},
      // Runtime fallback for designs saved before `contentWidth` existed.
      measuredContentWidth: 0,
    };
  },
  computed: {
    template() {
      return findYurtzeitenTemplate_N(
        this.design?.templateId || DEFAULT_YURTZEITEN_TEMPLATE_ID_N
      );
    },
    // The finder safely resolves stale/unknown stored ids to Stacked. Use its
    // real id for settings too so the fallback stays internally consistent.
    templateId() {
      return this.template.id;
    },
    normalizedSettings() {
      return normalizeYurtzeitenDesignSettings_N(this.design?.settings);
    },
    activeSettings() {
      return this.normalizedSettings[this.templateId]
        || defaultYurtzeitenTemplateSettings_N(this.templateId);
    },
    storedContentWidth() {
      return normalizeYurtzeitenContentWidth_N(this.activeSettings?.contentWidth);
    },
    renderSettings() {
      const contentWidth = this.storedContentWidth
        || normalizeYurtzeitenContentWidth_N(this.measuredContentWidth);
      return {
        ...this.activeSettings,
        cardPadding: normalizeYurtzeitenCardPadding_N(this.activeSettings?.cardPadding),
        spacing: normalizeYurtzeitenCardSpacing_N(this.activeSettings?.spacing),
        ...(contentWidth ? { contentWidth } : {}),
      };
    },
    widthProbeStyle() {
      return {
        maxWidth: `${this.authoredCardWidth}px`,
        padding: this.renderSettings.cardPadding,
      };
    },
    widthProbeKey() {
      return `${this.storedContentWidth || 'probe'}|${this.activeSettings?.html || ''}|${this.renderSettings.cardPadding}`;
    },
    // Read from the design as a whole rather than from the active template's
    // settings alone — see `resolveYurtzeitenDisplayColumns_N`. Display.vue
    // pages the records with this same resolver.
    columns() {
      return resolveYurtzeitenDisplayColumns_N(this.design);
    },
    authoredCardWidth() {
      const padding = getYurtzeitenCardPaddingParts_N(this.renderSettings?.cardPadding);
      return YURTZEITEN_TEMPLATE_CONTENT_WIDTH + padding.horizontal * 2;
    },
    cardSpacing() {
      return normalizeYurtzeitenCardSpacing_N(this.renderSettings?.spacing);
    },
    // The height ONE card has to fit into: the artboard's, shared between the
    // rows of cards this page actually draws. The pager never builds more cards
    // than the design's rows × columns, so a page of one row keeps the whole
    // artboard and a page of three gives each row a third of it, less the
    // authored spacing between them.
    //
    // Deliberately NOT reduced by the loop padding `verticalScrolling` adds
    // below. That padding is there because the row overflows, and a note is
    // given its window because the row overflows: budget the one against the
    // other and the board has a cycle — the note is windowed, the row fits, the
    // padding goes, the budget grows, the note is let out, the row overflows
    // again. A card's budget answers to the artboard and to nothing that a
    // window this component applies can change.
    cardAvailableHeight() {
      const height = Number(this.viewportHeight) || 0;
      if (!(height > 0)) return 0;
      const rows = this.pageRows;
      return Math.max((height - this.cardSpacing * (rows - 1)) / rows, 1);
    },
    // The rows of cards on this page: a full page has the design's row count, a
    // short last page only as many as its cards fill.
    pageRows() {
      return Math.max(Math.ceil(this.cards.length / this.columns), 1);
    },
    // More cards than one row holds — the page wraps onto further rows.
    isMultiRow() {
      return this.cards.length > this.columns;
    },
    // The width a column has to be. Measured from the cards themselves — a design
    // is drawn at its content's width, which is usually far narrower than the
    // authored maximum, and sizing columns to that maximum would leave the
    // difference sitting between the cards as extra gap. The authored width is
    // only the fallback for the first paint, before anything has measured.
    effectiveCardWidth() {
      const measured = this.cards
        .map((card) => Number(this.cardWidths[card.key]) || 0)
        .filter((width) => width > 0);
      return measured.length ? Math.max(...measured) : this.authoredCardWidth;
    },
    // Two cards on a three-column row. They stand in the outer columns, where
    // a full row's would, with the middle column empty — see `getCardStyle`.
    isOuterPair() {
      return this.columns === 3 && this.cards.length === 2;
    },
    // The same pair closing a multi-row page: the last row of a three-column
    // board holding two cards keeps them under the outer columns above it.
    hasOuterPairLastRow() {
      return this.columns === 3 && this.cards.length % 3 === 2;
    },
    // One card's width on a horizontal artboard: an equal share of the artboard
    // across the design's columns, never wider than the row's own cards.
    horizontalCardWidth() {
      const viewportWidth = Math.max(Number(this.viewportWidth) || 0, 1);
      return Math.min(
        Math.max((viewportWidth - this.cardSpacing * (this.columns - 1)) / this.columns, 1),
        this.rowCardWidth
      );
    },
    // The card width the row is laid out for. Normally the measured width
    // above, so a row of narrow designs closes up to the authored spacing. The
    // outer pair is the exception: nothing stands between its two cards for
    // that spacing to be read against, and tightening the row to whichever two
    // cards happen to be on the page drew them narrower and pulled in, off the
    // marks a page of three lands on. It is sized as a full row is instead — at
    // the authored card width, the most any full row's cards can measure — so
    // its two cards sit exactly where, and as wide as, a full row's outer two.
    rowCardWidth() {
      return this.isOuterPair
        ? Math.max(this.authoredCardWidth, this.effectiveCardWidth)
        : this.effectiveCardWidth;
    },
    // The row is capped at the width its cards occupy and centred, so the space
    // between them is the authored "Between Cards" value and nothing else.
    gridMaxWidth() {
      return (
        this.rowCardWidth * this.columns
        + this.cardSpacing * (this.columns - 1)
      );
    },
    entries() {
      return this.records
        .filter(Boolean)
        .map((record, index) => {
          const yahrzeitDate = record.yahrzeitDate || record.dateOfPassing;
          // The record's own halves, with the whole name joined from them when
          // the record predates the split and carries only `name`.
          const { firstName, lastName } = toYurtzeitenNameParts(record);
          const englishName =
            record.name || record.englishName || yurtzeitenFullName(firstName, lastName);
          const hebrewName = record.hebrewName || record.nameHebrew || '';
          const gregorianDate = this.gregorianLabel(yahrzeitDate);
          const hebrewDate = this.hebrewLabel(record.hebrewDate || yahrzeitDate);
          const comment = record.comment || record.notes || '';
          return {
            // Paired with the slot rather than keyed by id alone: keys only
            // have to be unique within the page, and the pairing keeps them so
            // even if the same record ever reached the list twice.
            key: `${record.id || 'record'}-${index}`,
            english: englishName,
            englishName,
            firstName,
            lastName,
            name: englishName,
            hebrew: hebrewName,
            hebrewName,
            gregorian: gregorianDate,
            gregorianDate,
            hebrewDate,
            image: this.imageUrl(record.image || record.photo),
            comment,
            notes: comment,
          };
        });
    },
    // A template owns the number of people drawn on one card, and the design's
    // column count owns how many cards stand side by side. The split honours
    // both: cards fill to the template's capacity, but a list too short to reach
    // the chosen column count is spread across the row instead of collapsing
    // into one card. Unlike the editor preview, production never pads with
    // sample rows — every card here holds real records only.
    cards() {
      const entriesPerCard = getYurtzeitenCardCapacity_N(this.renderSettings?.html || '');
      const sizes = buildYurtzeitenDisplayCardSizes_N(
        this.entries.length,
        entriesPerCard,
        this.columns
      );

      const cards = [];
      let start = 0;

      sizes.forEach((size, index) => {
        const entries = this.entries.slice(start, start + size);
        start += size;
        cards.push({
          key: `${this.templateId}-${entries[0]?.key || index}`,
          entries,
        });
      });

      return cards;
    },
    // From three columns up the row uses 2 × columns equal sub-tracks and each
    // card spans two — the same geometry as the Minyanim card grid — which
    // permits genuinely centred trailing cards without changing a card's width.
    gridStyle() {
      if (this.horizontal && !this.isMultiRow) {
        const viewportWidth = Math.max(Number(this.viewportWidth) || 0, 1);
        const gap = this.cardSpacing;
        const cardWidth = this.horizontalCardWidth;
        // What this page's cards actually occupy, against the artboard width
        // Display.vue measured for us. `viewportWidth` is the same box the
        // overflow check compares the rendered track against, so a row that is
        // not wider than it here is not wider than it there either.
        const contentWidth =
          this.cards.length * cardWidth + gap * Math.max(this.cards.length - 1, 0);

        // A row that fits has nothing to scroll. It must also not MEASURE wider
        // than the artboard: the stretch to the authored row width and the gap
        // that spaces one marquee copy from the next both exist only for the
        // loop, and either one would tip the overflow check over and have the
        // row duplicated into a marquee — which is what put the same card on
        // screen twice.
        if (contentWidth <= viewportWidth) {
          // Two cards on a three-column strip stand at its two ends with the
          // middle column empty, as they do in the grid below. The strip is
          // made the width of the full three-card row for that — a width the
          // card width above was already derived from, so it is never more than
          // the artboard, still fits, and is never duplicated into a marquee.
          if (this.isOuterPair) {
            const rowWidth = Math.min(viewportWidth, cardWidth * 3 + gap * 2);
            return {
              display: 'flex',
              width: `${rowWidth}px`,
              maxWidth: `${viewportWidth}px`,
              gap: `${gap}px`,
              justifyContent: 'space-between',
              '--yzd-horizontal-card-width': `${cardWidth}px`,
            };
          }

          return {
            display: 'flex',
            width: 'max-content',
            maxWidth: `${viewportWidth}px`,
            gap: `${gap}px`,
            justifyContent: 'center',
            '--yzd-horizontal-card-width': `${cardWidth}px`,
          };
        }

        return {
          display: 'flex',
          width: 'max-content',
          minWidth: `${Math.min(viewportWidth, this.effectiveCardWidth * this.columns + gap * (this.columns - 1))}px`,
          gap: `${gap}px`,
          // The outer marquee duplicates this whole strip. Include the normal
          // card gap at the copy boundary as part of each period.
          paddingInlineEnd: `${gap}px`,
          justifyContent: 'flex-start',
          '--yzd-horizontal-card-width': `${cardWidth}px`,
        };
      }

      const verticalPeriodStyle = this.verticalScrolling
        ? { paddingBottom: `${this.cardSpacing}px` }
        : {};

      // A multi-row page on a horizontal artboard is laid out as the same grid
      // below, sized to exactly one row of `horizontalCardWidth` cards so it
      // never measures wider than the artboard and is never duplicated into a
      // marquee. The item around it is max-content, so the width is definite.
      const widthStyle = this.horizontal
        ? {
          width: `${Math.min(
            Math.max(Number(this.viewportWidth) || 0, 1),
            this.horizontalCardWidth * this.columns + this.cardSpacing * (this.columns - 1)
          )}px`,
          marginInline: 'auto',
        }
        : {
          maxWidth: `${this.gridMaxWidth}px`,
          marginInline: 'auto',
        };

      if (this.columns >= 3) {
        return {
          gridTemplateColumns: `repeat(${getCenteredCardTemplateColumns(this.columns)}, minmax(0, 1fr))`,
          columnGap: `${this.cardSpacing}px`,
          rowGap: `${this.cardSpacing}px`,
          ...widthStyle,
          ...verticalPeriodStyle,
        };
      }

      return {
        gridTemplateColumns: `repeat(${this.columns}, minmax(0, 1fr))`,
        gap: `${this.cardSpacing}px`,
        ...widthStyle,
        ...verticalPeriodStyle,
      };
    },
  },
  methods: {
    observeWidthProbe() {
      const probe = this.$refs.widthProbeCanvas;
      if (!probe) {
        if (this.widthProbeObserver) this.widthProbeObserver.disconnect();
        this.widthProbeObserver = null;
        this.observedWidthProbe = null;
        return;
      }
      if (typeof ResizeObserver === 'undefined') return;
      if (this.observedWidthProbe === probe) return;
      if (this.widthProbeObserver) this.widthProbeObserver.disconnect();
      this.widthProbeObserver = new ResizeObserver(this.scheduleWidthProbeMeasure);
      this.widthProbeObserver.observe(probe);
      this.observedWidthProbe = probe;
    },
    scheduleWidthProbeMeasure() {
      if (this.widthProbeFrame) return;
      this.widthProbeFrame = requestAnimationFrame(() => {
        this.widthProbeFrame = null;
        this.measureWidthProbe();
      });
    },
    measureWidthProbe() {
      const probe = this.$refs.widthProbeCanvas;
      if (!probe || typeof window === 'undefined') return;
      const style = window.getComputedStyle(probe);
      const horizontalPadding =
        (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      const contentBoxWidth = probe.clientWidth - horizontalPadding;
      if (!(contentBoxWidth > 0)) return;
      const width = Math.round(contentBoxWidth);
      const normalized = normalizeYurtzeitenContentWidth_N(width) || 0;
      if (normalized !== this.measuredContentWidth) this.measuredContentWidth = normalized;
    },
    onCardNaturalWidth(key, width) {
      const measured = Math.ceil(Number(width) || 0);
      if (measured > 0 && this.cardWidths[key] !== measured) {
        this.cardWidths[key] = measured;
      }
    },
    gregorianLabel(value) {
      const date = toWallClockDate(value);
      if (!date) return '';
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
    hebrewLabel(value) {
      const date = toWallClockDate(value);
      if (!date) return '';
      const label = filters.convertToHebrewDate(date);
      return label === '-' ? '' : label;
    },
    imageUrl(value) {
      const image = String(value || '').trim();
      if (!image) return null;

      // Normal records carry a filename. Preserve values already resolved by
      // the backend or a migration, including data/blob URLs.
      if (/^(?:[a-z][a-z\d+.-]*:|\/\/|\/)/i.test(image)) return image;
      return `${STORAGE_URL}/${YURTZEITEN_IMAGES_DIR}/${image}`;
    },
    getCardStyle(index) {
      if (this.horizontal && !this.isMultiRow) {
        return {
          flex: '0 0 var(--yzd-horizontal-card-width)',
          width: 'var(--yzd-horizontal-card-width)',
        };
      }

      // Exactly two cards on a three-column row stand in the first and last
      // columns with the middle one left empty, so the row keeps the shape of
      // the three-column layout it was designed as rather than closing up
      // into a centred pair. On a multi-row page that is the last row, and its
      // two cards line up under the outer columns of the full rows above.
      if (this.hasOuterPairLastRow && index >= this.cards.length - 2) {
        return {
          gridColumn: index === this.cards.length - 2 ? '1 / span 2' : '5 / span 2',
          gridRow: String(Math.floor(index / 3) + 1),
        };
      }

      // Every other short row is centred: a lone trailing card on two columns,
      // and a partial last row from three columns up — Custom counts included.
      return getCenteredCardPlacement(this.columns, index, this.cards.length, this.cardSpacing);
    },
  },
  watch: {
    widthProbeKey() {
      this.measuredContentWidth = 0;
      this.$nextTick(() => {
        this.observeWidthProbe();
        this.scheduleWidthProbeMeasure();
      });
    },
    cards() {
      this.$nextTick(() => {
        this.observeWidthProbe();
        this.scheduleWidthProbeMeasure();
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.observeWidthProbe();
      this.scheduleWidthProbeMeasure();
    });
  },
  beforeUnmount() {
    if (this.widthProbeFrame) {
      cancelAnimationFrame(this.widthProbeFrame);
      this.widthProbeFrame = null;
    }
    if (this.widthProbeObserver) {
      this.widthProbeObserver.disconnect();
      this.widthProbeObserver = null;
      this.observedWidthProbe = null;
    }
  },
};
</script>

<style scoped>
.yzd-runtime {
  display: grid;
  align-items: start;
  width: 100%;
  height: auto;
  white-space: normal;
}

.yzd-runtime__card {
  min-width: 0;
  white-space: normal;
}

/* One inert, out-of-flow copy of the authored HTML for legacy width recovery.
   It is deliberately unconstrained by the display column around it. */
.yzd-width-probe {
  position: fixed;
  top: 0;
  left: 0;
  visibility: hidden;
  pointer-events: none;
  width: max-content;
  min-width: 120px;
  box-sizing: border-box;
  border: 1px solid transparent;
}

.yzd-width-probe__content,
.yzd-width-probe__document {
  width: max-content;
  max-width: 100%;
}
</style>
